import { ref } from '@nuxtjs/composition-api'

export function useSlideFacet() {
  const containerRef = ref<HTMLElement>()
  const facetItemRef = ref<HTMLCollectionOf<Element>>()

  const showLeftScrollButton = ref<boolean>(true)
  const showRightScrollButton = ref<boolean>(true)
  const visibleCassettesIndex = ref<number[]>([])

  const setItemRef = (containerNode: HTMLElement, childNode: HTMLCollectionOf<Element>): void => {
    containerRef.value = containerNode
    facetItemRef.value = childNode
  }

  const onAttachedCassetteIntersect = (e: IntersectionObserverEntry, index: number) => {
    if (index === 0) {
      showLeftScrollButton.value = !e.isIntersecting
    }

    if (index === facetItemRef.value.length - 1) {
      showRightScrollButton.value = !e.isIntersecting
    }

    if (e.isIntersecting) {
      visibleCassettesIndex.value.push(index)
    } else {
      const deleteIndex = visibleCassettesIndex.value.indexOf(index)
      if (deleteIndex >= 0) {
        visibleCassettesIndex.value.splice(deleteIndex, 1)
      }
    }
  }

  const getAttachmentCassettes = () => {
    let attachmentCassettes = Array.from(facetItemRef.value)
    if (!attachmentCassettes.length) return null
    return attachmentCassettes
  }

  const scrollLeftCassettes = (): void => {
    const draggableContainer = containerRef.value
    const attachmentCassettes = getAttachmentCassettes()
    if (!draggableContainer || !attachmentCassettes) {
      return
    }

    const { left } = draggableContainer.getBoundingClientRect()
    const overflowLeftPositions = attachmentCassettes
      .flatMap(v => (v.getBoundingClientRect().left < left ? v.getBoundingClientRect().left : []))
      .sort()
      .slice(-visibleCassettesIndex.value.length)
    if (overflowLeftPositions.length > 0) {
      const overflowLeftMax = Math.min(...overflowLeftPositions)
      draggableContainer.scrollTo({
        left: draggableContainer.scrollLeft - (left - overflowLeftMax),
        behavior: 'smooth'
      })
    }
  }

  const scrollRightCassettes = (): void => {
    const draggableContainer = containerRef.value
    const attachmentCassettes = getAttachmentCassettes()

    if (!draggableContainer || !attachmentCassettes) return
    const { right } = draggableContainer.getBoundingClientRect()
    const overflowRightPositions = attachmentCassettes
      .flatMap(
        v => (v.getBoundingClientRect().right > right ? v.getBoundingClientRect().right : [])
      )
      .sort()
      .slice(0, visibleCassettesIndex.value.length)
    if (overflowRightPositions.length > 0) {
      const overflowRightMin = Math.max(...overflowRightPositions)
      draggableContainer.scrollTo({
        left: draggableContainer.scrollLeft + (overflowRightMin - right),
        behavior: 'smooth'
      })
    }
  }

  return {
    showLeftScrollButton,
    showRightScrollButton,
    setItemRef,
    scrollLeftCassettes,
    scrollRightCassettes,
    onAttachedCassetteIntersect
  }
}

export function useMouseDrug() {
  const draggableContainerRef = ref<HTMLElement>()
  const isMouseDown = ref<boolean>(false)
  const isScrolling = ref<boolean>(false)
  const lastMousePositionX = ref<number>(0)

  /**
   * ドラッグ可能にする対象のDOMを設定
   */
  const setContainer = (node: HTMLElement): void => {
    draggableContainerRef.value = node
  }

  /**
   * マウスによるスクロールを可能にするための処理
   * mousedown時にそのマウスの位置を保持＆スクロール状態を有効化
   */
  const onMouseDownArea = (e: MouseEvent): void => {
    isMouseDown.value = true
    lastMousePositionX.value = e.x
  }

  /**
   * マウスによるスクロールを可能にするための処理
   * mousemove時にマウスを動かした分だけスクロールさせる
   */
  const onMouseMoveArea = (e: MouseEvent): void => {
    if (!isMouseDown.value) {
      return
    }
    isScrolling.value = true
    if (draggableContainerRef.value) {
      const { scrollLeft } = draggableContainerRef.value
      const scrollDelta = e.x - lastMousePositionX.value
      lastMousePositionX.value = e.x
      draggableContainerRef.value.scrollTo(scrollLeft - scrollDelta, 0)
    }
  }

  /**
   * マウスイベントの状態を初期化
   */
  const resetMouseState = (): void => {
    isScrolling.value = false
    isMouseDown.value = false
  }

  return {
    setContainer,
    onMouseDownArea,
    onMouseMoveArea,
    resetMouseState,
    isScrolling
  }
}
